import {CustomNode, Transition, WorkflowConfig} from "../../config";

const RESERVED_STATES = {
    MAKER: 'MAKER',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};

const RESERVED_EVENTS = {
    SUBMIT: 'SUBMIT',
};

export function createFlowFromConfig(config: WorkflowConfig): WorkflowConfig {
    const transitions: Transition[] = [];
    // @ts-ignore
    const states = sortStatesByLevel(config.states);

    for (let i = 0; i < states.length; i++) {
        const currentState = states[i];

        switch (currentState.type) {
            case 'maker':
                processMakerState(currentState, states, transitions);
                break;
            case 'validator':
                processValidatorState(currentState, states, transitions);
                break;
            case 'router':
                processRouterState(currentState, states, transitions);
                break;
            case 'executor':
                processExecutorState(currentState, states, transitions);
                break;
        }
    }

    // Validate the workflow configuration
    validateWorkflowConfiguration(states, transitions);

    return {
        ...config,
        // @ts-ignore
        states: states.map(state => ({
            ...state,
            id: formatStateName(state.id),
        })),
        transitions,
    };
}

function sortStatesByLevel(states: CustomNode[]): CustomNode[] {
    return states.sort((a, b) => {
        // Handle special cases first
        if (a.type === 'maker') return -1;
        if (b.type === 'maker') return 1;
        if (a.type === 'approved' || a.type === 'rejected') return 1;
        if (b.type === 'approved' || b.type === 'rejected') return -1;

        // Sort by level for regular nodes
        return (a.data.level || 0) - (b.data.level || 0);
    });
}

function processMakerState(currentState: CustomNode, states: CustomNode[], transitions: Transition[]) {
    const actions = currentState.data.actions || [];
    const stateName = formatStateName(currentState.id);

    // Always add the default SUBMIT transition to the next state
    const nextState = findNextState(currentState, states);
    if (nextState) {
        transitions.push({
            from: stateName,
            to: formatStateName(nextState.id),
            event: RESERVED_EVENTS.SUBMIT,
        });
    }

    // Process defined actions for maker nodes (when workflows return to maker)
    for (const action of actions) {
        // Use the action's event property if it exists, otherwise generate a default event name
        const event = action.event || `${stateName}_${action.name}`;
        let destination: string;

        switch (action.name) {
            case 'APPROVE':
                // For maker APPROVE, move to the next state in the workflow
                const approveNextState = findNextState(currentState, states, ['rejected']);
                destination = approveNextState ? approveNextState.id : RESERVED_STATES.APPROVED;
                break;
            case 'REJECT':
                destination = RESERVED_STATES.REJECTED;
                break;
            case 'RETURN':
                // For maker RETURN, this could be returning to a previous state or staying at maker
                // Default to staying at maker for now, but this could be customized
                destination = stateName;
                break;
            case 'REROUTE':
                destination = action.nextNode || findNextState(currentState, states)?.id || '';
                break;
            default:
                // For custom actions, default to next state
                destination = findNextState(currentState, states)?.id || '';
        }

        if (destination) {
            transitions.push({
                from: stateName,
                to: formatStateName(destination),
                event,
            });
        }
    }
}

function processValidatorState(currentState: CustomNode, states: CustomNode[], transitions: Transition[]) {
    const actions = currentState.data.actions || [];
    const stateName = formatStateName(currentState.id);

    // Process defined actions
    for (const action of actions) {
        // Use the action's event property if it exists, otherwise generate a default event name
        const event = action.event || `${stateName}_${action.name}`;
        let destination: string;

        switch (action.name) {
            case 'APPROVE':
                const nextState = findNextState(currentState, states, ['rejected']);
                destination = nextState ? nextState.id : RESERVED_STATES.APPROVED;
                break;
            case 'REJECT':
                destination = RESERVED_STATES.REJECTED;
                break;
            case 'RETURN':
                destination = RESERVED_STATES.MAKER;
                break;
            case 'REROUTE':
                destination = action.nextNode || findNextState(currentState, states)?.id || '';
                break;
            default:
                destination = findNextState(currentState, states)?.id || '';
        }

        if (destination) {
            transitions.push({
                from: stateName,
                to: formatStateName(destination),
                event,
            });
        }
    }

    // Add default transitions if not all actions are covered
    if (!actions.some(action => action.name === 'APPROVE')) {
        const nextState = findNextState(currentState, states);
        if (nextState) {
            transitions.push({
                from: stateName,
                to: formatStateName(nextState.id),
                event: `${stateName}_APPROVED`,
            });
        }
    }

    if (!actions.some(action => action.name === 'REJECT')) {
        transitions.push({
            from: stateName,
            to: RESERVED_STATES.REJECTED,
            event: `${stateName}_REJECTED`,
        });
    }
}

function processRouterState(currentState: CustomNode, states: CustomNode[], transitions: Transition[]) {
    const decisionRules = currentState.data.decisionRules || [];
    const stateName = formatStateName(currentState.id);

    // Process decision rules
    for (const rule of decisionRules) {
        transitions.push({
            from: stateName,
            to: formatStateName(rule.nextNode),
            event: rule.event,
        });
    }

    // Add default transition
    if (currentState.data.defaultNextNode) {
        transitions.push({
            from: stateName,
            to: formatStateName(currentState.data.defaultNextNode),
            event: currentState.data.defaultEvent || 'DEFAULT',
        });
    } else {
        const nextState = findNextState(currentState, states);
        if (nextState) {
            transitions.push({
                from: stateName,
                to: formatStateName(nextState.id),
                event: 'DEFAULT',
            });
        }
    }
}

function processExecutorState(currentState: CustomNode, states: CustomNode[], transitions: Transition[]) {
    const stateName = formatStateName(currentState.id);
    const nextState = findNextState(currentState, states);

    // Add default execution transition
    if (nextState) {
        transitions.push({
            from: stateName,
            to: formatStateName(nextState.id),
            event: 'EXECUTE',
        });
    }

    // Add transitions for custom events
    const customTransitions = currentState.data.transitions || [];
    for (const transition of customTransitions) {
        transitions.push({
            from: stateName,
            to: formatStateName(transition.nextNode),
            event: transition.event,
        });
    }
}

function findNextState(
    currentState: CustomNode,
    states: CustomNode[],
    excludeTypes: string[] = []
): CustomNode | undefined {
    const currentIndex = states.findIndex(state => state.id === currentState.id);
    return states.slice(currentIndex + 1).find(state =>
        state.data.level > currentState.data.level &&
        // @ts-ignore
        !excludeTypes.includes(state.type)
    );
}

function validateWorkflowConfiguration(states: CustomNode[], transitions: Transition[]) {
    // Ensure there is exactly one maker node
    const makerNodes = states.filter(state => state.type === 'maker');
    if (makerNodes.length !== 1) {
        throw new Error('Workflow must have exactly one maker node');
    }

    // Ensure there is at least one end state (approved or rejected)
    const endStates = states.filter(state => state.type === 'approved' || state.type === 'rejected');
    if (endStates.length === 0) {
        throw new Error('Workflow must have at least one end state (approved or rejected)');
    }

    // Validate that all nodes are reachable
    const reachableStates = new Set<string>([RESERVED_STATES.MAKER]);
    let changed = true;

    while (changed) {
        changed = false;
        for (const transition of transitions) {
            if (reachableStates.has(transition.from) && !reachableStates.has(transition.to)) {
                reachableStates.add(transition.to);
                changed = true;
            }
        }
    }

    const unreachableStates = states
        .map(state => formatStateName(state.id))
        .filter(stateId => !reachableStates.has(stateId));

    if (unreachableStates.length > 0) {
        throw new Error(`Unreachable states detected: ${unreachableStates.join(', ')}`);
    }

    // Validate level ordering
    for (let i = 0; i < states.length - 1; i++) {
        const currentState = states[i];
        const nextState = states[i + 1];

        if (currentState.type !== 'maker' &&
            nextState.type !== 'approved' &&
            nextState.type !== 'rejected' &&
            currentState.data.level >= nextState.data.level) {
            throw new Error(`Invalid level ordering between ${currentState.id} and ${nextState.id}`);
        }
    }
}

function formatStateName(name: string): string {
    return name.toUpperCase().replace(/\s+/g, '_');
}

export { processMakerState, processValidatorState, processRouterState, processExecutorState };